import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import AddIcon from '@mui/icons-material/AddOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import SearchableSelect from '@/components/common/SearchableSelect';
import StatusChip from '@/components/common/StatusChip';
import { IfPermitted } from '@/components/auth/RequirePermission';
import { useDateFormat } from '@/hooks/useDateFormat';
import { useProcessConfiguration } from '@/hooks/useProcessConfiguration';
import { PERMISSIONS } from '@/types/auth';
import type { WarehouseDto } from '@/types/organization';
import type { ClientDto, CustomerDto, MaterialDto, PagedResult, SupplierDto } from '@/types/materials';
import type { CreateReturnNoteRequest, ReturnNoteDto, ReturnNoteLineInput, UpdateReturnNoteRequest } from '@/types/adjustments';

const emptyLine: ReturnNoteLineInput = { materialId: '', qty: 0, reason: null };

export default function ReturnNotesPage() {
  const { t } = useTranslation('returnNote');
  const { formatDate } = useDateFormat();
  const queryClient = useQueryClient();

  const { data: returns = [], isLoading } = useQuery({
    queryKey: ['return-notes'],
    queryFn: async () => (await apiClient.get<ReturnNoteDto[]>('/return-notes')).data,
  });
  const { data: warehouses = [] } = useQuery({
    queryKey: ['warehouses'],
    queryFn: async () => (await apiClient.get<WarehouseDto[]>('/warehouses')).data,
  });
  const { data: customers = [] } = useQuery({
    queryKey: ['customers'],
    queryFn: async () => (await apiClient.get<CustomerDto[]>('/customers')).data,
  });
  const { data: suppliers = [] } = useQuery({
    queryKey: ['suppliers'],
    queryFn: async () => (await apiClient.get<SupplierDto[]>('/suppliers')).data,
  });
  const { data: materialsPage } = useQuery({
    queryKey: ['materials', 'all-for-picker'],
    queryFn: async () => (await apiClient.get<PagedResult<MaterialDto>>('/materials', { params: { page: 1, pageSize: 200 } })).data,
  });
  const materials = materialsPage?.items ?? [];
  const { data: config } = useProcessConfiguration();
  const { data: clients = [] } = useQuery({
    queryKey: ['clients'],
    queryFn: async () => (await apiClient.get<ClientDto[]>('/clients')).data,
    enabled: config?.thirdPartyLogisticsEnabled ?? false,
  });

  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [warehouseId, setWarehouseId] = useState('');
  const [returnType, setReturnType] = useState('FromCustomer');
  const [customerId, setCustomerId] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [lines, setLines] = useState<ReturnNoteLineInput[]>([{ ...emptyLine }]);

  const resetForm = () => {
    setEditingId(null);
    setWarehouseId('');
    setReturnType('FromCustomer');
    setCustomerId('');
    setSupplierId('');
    setLines([{ ...emptyLine }]);
  };

  // Only usable while Status is still "Draft" — mirrors ReturnNoteService.UpdateAsync's guard.
  const openEdit = (r: ReturnNoteDto) => {
    setEditingId(r.id);
    setWarehouseId(r.warehouseId);
    setReturnType(r.returnType);
    setCustomerId(r.customerId ?? '');
    setSupplierId(r.supplierId ?? '');
    setLines(r.lines.map((l) => ({ materialId: l.materialId, qty: l.qty, reason: l.reason, clientId: l.clientId })));
    setOpen(true);
  };

  const createMutation = useMutation({
    mutationFn: () => {
      const cleanLines = lines.filter((l) => l.materialId);
      if (editingId) {
        const payload: UpdateReturnNoteRequest = {
          warehouseId, returnType, customerId: returnType === 'FromCustomer' ? customerId || null : null,
          supplierId: returnType === 'ToSupplier' ? supplierId || null : null, lines: cleanLines,
        };
        return apiClient.put(`/return-notes/${editingId}`, payload);
      }
      const payload: CreateReturnNoteRequest = {
        warehouseId, returnType, customerId: returnType === 'FromCustomer' ? customerId || null : null,
        supplierId: returnType === 'ToSupplier' ? supplierId || null : null, lines: cleanLines,
      };
      return apiClient.post('/return-notes', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['return-notes'] });
      setOpen(false);
    },
  });

  const postMutation = useMutation({
    mutationFn: (id: string) => apiClient.post(`/return-notes/${id}/post`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['return-notes'] }),
  });

  const columns: DataTableColumn<ReturnNoteDto>[] = [
    { field: 'returnNumber', headerName: t('table.number') },
    { field: 'returnType', headerName: t('table.type'), filterable: true },
    { field: 'warehouseName', headerName: t('table.warehouse'), filterable: true },
    { field: 'returnDate', headerName: t('table.date'), render: (r) => formatDate(r.returnDate) },
    { field: 'lines', headerName: t('table.lines'), render: (r) => r.lines.length },
    { field: 'status', headerName: t('table.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
    {
      field: 'action', headerName: '', render: (r) => (
        <IfPermitted permission={PERMISSIONS.RETURN_NOTE_MANAGE}>
          {r.status === 'Draft' && (
            <Stack direction="row" spacing={1}>
              <Button size="small" onClick={() => openEdit(r)}>
                {t('edit')}
              </Button>
              <Button size="small" variant="outlined" disabled={postMutation.isPending} onClick={() => postMutation.mutate(r.id)}>
                {t('post')}
              </Button>
            </Stack>
          )}
        </IfPermitted>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        actions={
          <IfPermitted permission={PERMISSIONS.RETURN_NOTE_MANAGE}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => { resetForm(); setOpen(true); }}>
              {t('add')}
            </Button>
          </IfPermitted>
        }
      />

      <DataTable columns={columns} rows={returns} loading={isLoading} searchable getRowId={(r) => r.id} exportFileName="return-notes" />

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? t('edit') : t('add')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={2}>
              <SearchableSelect
                label={t('form.warehouse')} value={warehouseId} onChange={setWarehouseId} fullWidth
                options={warehouses.map((w) => ({ value: w.id, label: w.name }))}
              />
              <TextField select label={t('form.returnType')} value={returnType} onChange={(e) => setReturnType(e.target.value)} fullWidth>
                <MenuItem value="FromCustomer">{t('form.fromCustomer')}</MenuItem>
                <MenuItem value="ToSupplier">{t('form.toSupplier')}</MenuItem>
              </TextField>
            </Stack>

            {returnType === 'FromCustomer' ? (
              <SearchableSelect
                label={t('form.customer')} value={customerId} onChange={setCustomerId} fullWidth
                options={[{ value: '', label: t('form.none') }, ...customers.map((c) => ({ value: c.id, label: c.name }))]}
              />
            ) : (
              <SearchableSelect
                label={t('form.supplier')} value={supplierId} onChange={setSupplierId} fullWidth
                options={[{ value: '', label: t('form.none') }, ...suppliers.map((s) => ({ value: s.id, label: s.name }))]}
              />
            )}

            {lines.map((line, idx) => (
              <Stack key={idx} direction="row" spacing={1} alignItems="center">
                <SearchableSelect
                  label={t('form.material')} value={line.materialId} fullWidth size="small"
                  onChange={(materialId) => setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, materialId } : l)))}
                  options={materials.map((m) => ({ value: m.id, label: `${m.code} — ${m.name}` }))}
                />
                <TextField
                  type="number" label={t('form.quantity')} value={line.qty} size="small" sx={{ minWidth: 120 }}
                  onChange={(e) => setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, qty: Number(e.target.value) } : l)))}
                />
                <TextField
                  label={t('form.reason')} value={line.reason ?? ''} size="small" sx={{ minWidth: 160 }}
                  onChange={(e) => setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, reason: e.target.value || null } : l)))}
                />
                {returnType === 'FromCustomer' && config?.thirdPartyLogisticsEnabled && (
                  <SearchableSelect
                    label={t('form.client')} value={line.clientId ?? ''} size="small" sx={{ minWidth: 160 }}
                    onChange={(clientId) => setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, clientId: clientId || null } : l)))}
                    options={clients.map((c) => ({ value: c.id, label: c.name }))}
                    helperText={t('form.clientHintNewBatch')}
                  />
                )}
                <IconButton size="small" onClick={() => setLines((prev) => prev.filter((_, i) => i !== idx))} disabled={lines.length <= 1}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
            <Button size="small" startIcon={<AddIcon />} onClick={() => setLines((prev) => [...prev, { ...emptyLine }])} sx={{ alignSelf: 'flex-start' }}>
              {t('form.addLine')}
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)}>{t('cancel')}</Button>
          <Button
            variant="contained"
            disabled={!warehouseId || lines.some((l) => !l.materialId || l.qty <= 0) || createMutation.isPending}
            onClick={() => createMutation.mutate()}
          >
            {t('save')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
